
import React from 'react';
import { Language, IntegrationApp, AppConfig } from '../../types';
import IntegrationsList from './IntegrationsList';
import IntegrationsHeader from './IntegrationsHeader';
import IntegrationsDashboard from './IntegrationsDashboard';
import IntegrationsSettings from './IntegrationsSettings';
import IntegrationsLive from './IntegrationsLive';

interface IntegrationsComponentProps {
  language: Language;
  config: AppConfig;
  apps: IntegrationApp[];
  selectedApp: IntegrationApp | null;
  setSelectedApp: (app: IntegrationApp | null) => void;
  activeTab: 'dashboard' | 'settings' | 'live';
  setActiveTab: (tab: 'dashboard' | 'settings' | 'live') => void;
  isSimulating: boolean;
  setIsSimulating: (simulating: boolean) => void;
  isConnecting: boolean;
  handleConnect: (appId: string) => void;
  updateAppConfig: (key: string, value: any) => void;
  userLocation: {lat: number, lng: number} | null;
  activeDestination: string;
  isNavigating: boolean;
  isAiSpeaking: boolean;
  aiMapResponse: string;
  navStepIndex: number;
  mockRouteEvents: string[];
  destinationInput: string;
  setDestinationInput: (val: string) => void;
  startNavigation: () => void;
  callStatus: 'incoming' | 'active' | 'ended';
  setCallStatus: (status: 'incoming' | 'active' | 'ended') => void;
  callDuration: number;
  callTranscript: {original: string, translated: string, isUser: boolean}[];
  formatTime: (s: number) => string;
}

const IntegrationsComponent: React.FC<IntegrationsComponentProps> = (props) => {
  const { 
    language, apps, selectedApp, setSelectedApp, activeTab, setActiveTab, 
    isSimulating, setIsSimulating, isConnecting, handleConnect, updateAppConfig,
    userLocation, activeDestination, isNavigating, isAiSpeaking, aiMapResponse,
    mockRouteEvents, navStepIndex, destinationInput, setDestinationInput, startNavigation,
    callStatus, setCallStatus, callDuration, callTranscript, formatTime
  } = props;

  if (!selectedApp) {
    return (
      <IntegrationsList 
        language={language}
        apps={apps}
        onSelectApp={setSelectedApp}
      />
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50 dark:bg-[#08080a]">
       <IntegrationsHeader 
         language={language}
         selectedApp={selectedApp}
         activeTab={activeTab}
         setActiveTab={setActiveTab}
         onBack={() => setSelectedApp(null)}
       />

       <div className="flex-1 overflow-y-auto custom-scrollbar p-6 lg:p-10">
          {activeTab === 'dashboard' && (
            <IntegrationsDashboard 
              language={language}
              selectedApp={selectedApp}
              isConnecting={isConnecting}
              handleConnect={handleConnect}
              destinationInput={destinationInput}
              setDestinationInput={setDestinationInput}
              startNavigation={startNavigation}
              setActiveTab={setActiveTab}
              setCallStatus={setCallStatus}
              setIsSimulating={setIsSimulating}
            />
          )}
          {activeTab === 'settings' && (
            <IntegrationsSettings 
              language={language}
              selectedApp={selectedApp}
              updateAppConfig={updateAppConfig}
            />
          )}
          {activeTab === 'live' && (
            <IntegrationsLive 
              language={language}
              selectedApp={selectedApp}
              userLocation={userLocation}
              activeDestination={activeDestination}
              isNavigating={isNavigating}
              isAiSpeaking={isAiSpeaking}
              aiMapResponse={aiMapResponse}
              navStepIndex={navStepIndex}
              mockRouteEvents={mockRouteEvents}
              isSimulating={isSimulating}
              setIsSimulating={setIsSimulating}
              callStatus={callStatus}
              setCallStatus={setCallStatus}
              callTranscript={callTranscript}
              setActiveTab={setActiveTab}
            />
          )}
       </div>
    </div>
  );
};

export default IntegrationsComponent;
